/**
 * 이상한 방 - 각자의 바꾸기 작업대 (`applyChange` · `undoChange` · `commitChanges` · 초안 합치기)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(바꾸기 1분) · 4.2절(선택 → 동사 → 즉시 실행) · 4.3절(거부는 오류가 아니라 선택 불가) ·
 *            7절("몇 개/60초가 사람에게 되는가" - 이 파일이 그 실험의 규칙 쪽이다)
 *            `이상한방-상태모델-v0.1.md` 5.1절(한 물건 한 번) · 5.3절(허용 동사) · 11.3절(변화 검증은 서버가 쥔다)
 *            로드맵 M4 "고정할 규칙" 1~5 · M11 규칙 2·3·8 · 로드맵 모델 2.6절
 *
 * ## 작업대가 좌석마다 하나다 (M11)
 *
 * M10까지는 한 라운드에 작업대가 하나였고 나머지는 기다렸다. 지금은 **전원이 동시에** 자기 구획을 고친다 -
 * `state.desks[id]`가 그 사람의 초안이고, 초안은 원본 + 자기 구획의 자기 변화뿐이다. 남의 초안은 어떤 경로로도
 * 가지 않는다(`matchView.roomsFor`가 자기 것만 열고 호스트도 받는 사람의 작업대에서 꺼낸다). 남에게 가는 것은
 * 개수(`PublicPlayer.changeUsed`)뿐이다.
 *
 * ## 이 파일에는 거부 사유의 비교식이 없다 - `editor.ts` 머리말과 같은 규약
 *
 * 동사의 집행자는 `core/verbs`(`applyVerbRequest` → `applyAdd`·`applyRemove`·`applyMove`·`applyRotate`·`applyRecolor`) 하나다. 5.1절(한 물건 한 번)도
 * 여기서 `touched`를 세지 않고 `VerbContext.changedIds` + `shouldEnforceOneChangePerObject`로 **verbs에게 맡긴다** - 생성기
 * (`changeGen`)가 같은 스위치로 같은 규칙을 집행하므로, 사람이 만든 변화와 시스템이 채운 변화가 한 집행자를 거친다.
 * **남의 구획도 같은 자리에서 답한다**(`VerbContext.allowedRect`) - "이건 내 구획이 아니다"를 여기 적으면 도구
 * (`input/changerTool`)가 같은 비교식을 한 벌 더 들어야 하고, 그러면 잠긴 팔레트와 호스트 판정이 갈리는 날이 온다.
 * 거부는 `VerbRejection`을 **그대로** `changeRejected{reason}`에 싣는다. 이 파일에 `'already-changed'` 같은 문자열이 한 번이라도
 * 나오면 거부 사유의 두 번째 사본이 생긴 것이다(`matchChange.test.ts`가 소스에서 검사한다).
 *
 * ## 게이트는 참조 동일, 규칙 위반은 효과
 *
 * 바꾸기 페이즈가 아니거나 · 작업대가 없거나 · 이미 커밋했거나 · 예산이 다 찼으면 **입력 상태 참조 그대로**다
 * (`matchState` 머리말 "권한 검사는 여기서 끝난다"). 뷰(`self.canChange` · `desk.budgetLeft`)가 이미 버튼을 잠갔을 것이므로
 * 여기까지 온 것은 지연이나 위장이다. 반면 verbs의 거부(경계 밖 · 남의 구획 · 점유 · 무변화 회전 · 이미 건드린 물건)는
 * **본인에게만** `changeRejected`로 알린다 - 낙관적으로 그려 둔 초안을 되돌릴 신호가 필요하다.
 *
 * ## 초안은 각자의 것, 개수는 전원의 것
 *
 * 수용마다 본인에게 `changeAccepted{verb, budgetLeft}`, 전원에게 `changerProgress{by, used, budget}`. 무엇을 건드렸는지는
 * 개수에 없다(M11 규칙 3). `by`가 붙어 있는 이유는 전원이 동시에 만들기 때문이다 - 없으면 옆 사람의 수용이 내 도구의
 * 대기 요청을 확정한다. `rooms` 메시지도 내지 않는다 - `roomsChanged`는 페이즈 경계의 신호다.
 *
 * ## 되돌리기는 배치 검사를 다시 받는다
 *
 * `undoChange{id}`는 그 물건을 `original`의 모습으로 되돌리고 `touched`에서 뺀다(예산 복구). 옮긴 물건의 원래 자리에 그새
 * 다른 것을 놓았을 수 있으므로 되돌아갈 자리를 `canPlace`로 다시 검사하고, 막히면 그 사유(`cell-occupied`)로 거부한다.
 * 그 길은 `core/verbs.restoreOriginal` 하나다(도구와 공유). 되돌아갈 자리는 내 구획 안이므로 구획 검사는 걸리지 않는다.
 *
 * ## 합치기는 원본 위에 각자의 변화만 얹는다
 *
 * 구획은 서로 겹치지 않고(벽 한 칸이 가른다) 각자 자기 구획 밖으로 나갈 수 없으므로, 초안 여럿을 합치는 데
 * 충돌 해소 규칙이 필요 없다 - 원본에서 시작해 각자의 `changes`가 가리키는 물건만 그 사람의 초안 모습으로 옮기면 된다.
 * 그래서 `mergeDrafts`에는 "누가 이겼는가"가 없다. 그 규칙이 필요해지는 순간은 구획이 겹치는 순간이고, 그건 평면도의 버그다.
 *
 * ## 변화 id는 대상 id에서 만든다
 *
 * `desk-${targetId}`. 5.1절이 대상 하나에 변화 하나를 보장하므로 유일하고, 되돌린 뒤 다시 건드려도 같은 id가 되어 되돌리기
 * 카운터가 필요 없다. 생성기의 `chg-NNN`과는 접두사가 다르다 - 마감 채움이 슬롯 0부터 번호를 매기므로 접두사를 같이 쓰면
 * 사람의 첫 변화와 채움의 첫 변화가 같은 id를 갖고 `found`가 둘을 하나로 본다.
 *
 * 이 파일은 `core`·`catalog`·`game` 리프만 본다(`boundary.test.ts`) - 서버가 그대로 import 한다.
 */

import { roomAt, type CellRect, type FloorPlan } from '../../core/floorPlan'
import type { Cell3, InstanceId, ObjectInstance, PlayerId } from '../../core/types'
import { applyVerbRequest, changerVerbContext, restoreOriginal, type VerbContext, type VerbRejection } from '../../core/verbs'
import type { Change } from '../changeGen'
/** 걸기 값의 검사는 리프(`raise.ts`)의 것이다 - 여기 `unfoundAtLeast < 1` 같은 비교식을 적으면 걸기 규칙이 두 곳에 산다. */
import { normalizeWager } from '../raise'
import { activeDesk, budgetLeftOf, everyoneCommitted, hasBudgetLeft, withDesk } from './matchDesk'
import { finishChange } from './matchPhases'
import { idle, matchPlan, patchPlayer, playerOf, zoneFor } from './matchSelectors'
import type { ChangeDesk, ChangerWager, MatchChange, MatchDeps, MatchState, MatchStep, VerbRequest } from './matchTypes'

/* --- 상수 ------------------------------------------------------------------------ */

/** 사람이 만든 변화의 id 접두사(머리말 "변화 id는 대상 id에서 만든다"). */
const DESK_CHANGE_ID_PREFIX = 'desk-'

/* --- verbs 호출 ----------------------------------------------------------------- */

/**
 * 한 사람의 검증 문맥은 `core/verbs.changerVerbContext` 하나다 - 도구(`input/changerTool.ts`)와 같은 스위치를 같은
 * 함수에서 받는다(한 물건 한 번 켬 · 인당 배치 제한 끔 · 점유 맵 없음 · **자기 구획만**). 구획 사각형은 배정표의 값을
 * 그대로 넘긴다(`matchZones.ZoneAssignment.rect`) - 여기서 좌표를 만들면 경계가 두 곳에 적힌다.
 * 방 규칙은 `config.room`이 진실이다(`matchPhases` 머리말).
 *
 * **구획이 없는 좌석은 아무 데도 손댈 수 없다** - 넓이 0의 사각형을 넘긴다. 구획 수 < 좌석 수인 평면도(고정 평면도로
 * 도는 옛 픽스처)에서만 생기는 자리이고, 그 좌석이 `undefined`를 받아 "제한 없음"이 되면 남의 구획을 마음대로 고친다.
 * 규칙을 리듀서에 한 줄 더 적는 대신 같은 문(`allowedRect`)으로 답이 나오게 한다 - 거부 사유도 `out-of-bounds` 하나다.
 */
const NO_ZONE_RECT: CellRect = { x: 0, z: 0, sizeX: 0, sizeZ: 0 }

function verbContextFor(state: MatchState, desk: ChangeDesk, by: PlayerId, deps: MatchDeps): VerbContext {
  return changerVerbContext(
    deps.catalog,
    state.config.room,
    matchPlan(state, deps).bounds,
    new Set(desk.touched),
    zoneFor(state, deps, by)?.rect ?? NO_ZONE_RECT,
  )
}

/* --- 변화 기록 -------------------------------------------------------------------- */

function zoneOfCell(plan: FloorPlan, cell: Cell3): Pick<Change, 'zoneId'> {
  const room = roomAt(plan, cell.x, cell.z)
  return room === undefined ? {} : { zoneId: room.id }
}

/**
 * 수용된 요청을 `MatchChange` 한 줄로. 좌표 규약은 `changeGen.Change` 주석 그대로다(제거는 before만, 추가는 after만,
 * 이동은 둘 다, 제자리 동사는 before만). `difficulty`는 적지 않는다 - 난이도 채점은 생성기의 내부 곡선이고 사람이 만든
 * 것에는 배분 목표가 없다. 읽는 쪽(`cpu.difficultyRank`)은 없으면 `normal`로 본다.
 * `owner`는 만든 사람이다 - 자기 구획 안에서만 만들 수 있으므로 "누구 구획인가"와 같은 값이고, 그래서 배정표를
 * 다시 묻지 않는다.
 * `before`는 verbs가 `ok`를 낸 뒤의 값이라 `add`가 아니면 반드시 있다 - 없으면 계약 위반이라 던진다(`room.ts` 규약).
 */
function changeFor(
  request: VerbRequest,
  before: ObjectInstance | undefined,
  owner: PlayerId,
  plan: FloorPlan,
): MatchChange {
  if (request.verb === 'add') {
    const { obj } = request
    return {
      id: `${DESK_CHANGE_ID_PREFIX}${obj.id}`,
      verb: 'add',
      targetId: obj.id,
      afterCell: obj.cell,
      catalogId: obj.catalogId,
      owner,
      ...zoneOfCell(plan, obj.cell),
    }
  }
  if (before === undefined) {
    throw new Error(`matchChange: verbs가 수용한 '${request.id}'가 초안에 없다 - 상태가 손상됐다`)
  }
  const base: MatchChange = {
    id: `${DESK_CHANGE_ID_PREFIX}${before.id}`,
    verb: request.verb,
    targetId: before.id,
    beforeCell: before.cell,
    catalogId: before.catalogId,
    owner,
    ...zoneOfCell(plan, before.cell),
  }
  return request.verb === 'move' ? { ...base, afterCell: request.cell } : base
}

/* --- 효과 ------------------------------------------------------------------------ */

function rejected(state: MatchState, to: PlayerId, reason: VerbRejection): MatchStep {
  return { state, effects: [{ to, body: { type: 'changeRejected', reason } }] }
}

function progressed(state: MatchState, by: PlayerId, desk: ChangeDesk): MatchStep {
  return {
    state: withDesk(state, by, desk),
    effects: [{ to: 'all', body: { type: 'changerProgress', by, used: desk.changes.length, budget: desk.budget } }],
  }
}

/* --- 이벤트 ---------------------------------------------------------------------- */

/**
 * 변화 하나를 요청한다. 게이트(참조 동일) → verbs(거부는 효과) → 작업대에 기록.
 * `add`의 id가 초안에 이미 있으면 참조 동일이다 - `addObject`가 던지는 계약 위반이고, 와이어에서 온 값으로 호스트가 던지면 안 된다.
 * 없앤 물건의 id로 다시 놓는 것(한 대상에 변화 둘)은 verbs가 `already-changed`로 거부한다(`applyAdd` 주석).
 */
export function onApplyChange(state: MatchState, by: PlayerId, request: VerbRequest, deps: MatchDeps): MatchStep {
  const desk = activeDesk(state, by)
  if (desk === null || !hasBudgetLeft(desk)) return idle(state)
  if (request.verb === 'add' && desk.draft.objects.has(request.obj.id)) return idle(state)

  const before = request.verb === 'add' ? undefined : desk.draft.objects.get(request.id)
  const result = applyVerbRequest(desk.draft, request, by, verbContextFor(state, desk, by, deps))
  if (!result.ok) return rejected(state, by, result.reason)

  const change = changeFor(request, before, by, matchPlan(state, deps))
  const next: ChangeDesk = {
    ...desk,
    touched: [...desk.touched, change.targetId],
    draft: result.state,
    changes: [...desk.changes, change],
  }
  const step = progressed(state, by, next)
  return {
    state: step.state,
    // 본인의 확인이 전원 통지보다 먼저다 - 클라이언트가 초안을 굳힌 뒤 바의 점을 채운다.
    effects: [{ to: by, body: { type: 'changeAccepted', verb: change.verb, budgetLeft: budgetLeftOf(next) } }, ...step.effects],
  }
}

/**
 * 한 물건을 되돌린다(머리말 "되돌리기는 배치 검사를 다시 받는다"). 건드리지 않은 물건은 참조 동일 - 되돌릴 것이 없다.
 * 되돌리는 길 자체는 `core/verbs.restoreOriginal`이다 - 도구의 낙관적 되돌리기와 같은 함수라 두 쪽의 답이 갈릴 수 없다.
 */
export function onUndoChange(state: MatchState, by: PlayerId, id: InstanceId, deps: MatchDeps): MatchStep {
  const desk = activeDesk(state, by)
  if (desk === null || !desk.touched.includes(id)) return idle(state)

  const result = restoreOriginal(desk.draft, state.original, id, deps.catalog, matchPlan(state, deps).bounds)
  if (!result.ok) return rejected(state, by, result.reason)

  return progressed(state, by, {
    ...desk,
    touched: desk.touched.filter((touched) => touched !== id),
    draft: result.state,
    changes: desk.changes.filter((change) => change.targetId !== id),
  })
}

/**
 * 다 했다. 내 작업대를 잠그고, **전원이 끝났으면** 바꾸기가 그 자리에서 끝난다(`matchPhases.finishChange` -
 * 시간 종료와 같은 경로라 남은 예산은 구획마다 생성기가 채운다). 아직 손대는 사람이 남았으면 개수만 알린다 -
 * 기다리는 화면이 "누가 아직인가"를 그 값으로 그린다.
 *
 * `wager`(M7 "내 것 N개는 아무도 못 찾는다")는 좌석에 굳는다 - 작업대는 수색 진입에서 사라지고 점수는 결과 진입에서
 * 읽는다. 값은 `raise.normalizeWager`가 접는다(정수 · 1 이상 · 예산 이하, 아니면 걸지 않은 것) - 와이어에서 온 값이라
 * 모양을 믿을 수 없고, 잘못된 걸기로 커밋 자체를 거절하면 60초 동안 만든 것이 값 하나로 버려진다. 상한이 `budget`인
 * 이유는 그것이 **내 구획의** 이상한 곳 수이기 때문이다(채움까지 합쳐 정확히 그 수가 된다).
 */
export function onCommitChanges(state: MatchState, by: PlayerId, wager: ChangerWager | null, deps: MatchDeps): MatchStep {
  const desk = activeDesk(state, by)
  if (desk === null || playerOf(state, by) === undefined) return idle(state)

  const committed = withDesk(state, by, { ...desk, committed: true })
  const wagered = patchPlayer(committed, by, { wager: normalizeWager(wager, desk.budget) })
  if (!everyoneCommitted(wagered)) {
    return {
      state: wagered,
      effects: [{ to: 'all', body: { type: 'changerProgress', by, used: desk.changes.length, budget: desk.budget } }],
    }
  }
  return finishChange(wagered, deps)
}
